import * as z from "zod";
import {
  createOrderEditInputSchema,
  createOrderEditOutputSchema,
} from "./base";

/**
 * Schema for cancelling an order
 */

// Data schema for cancel order
const CancelOrderData_ = z.object({
  reason: z
    .enum(["CUSTOMER", "FRAUD", "INVENTORY", "DECLINED", "OTHER", "STAFF"])
    .optional()
    .describe("Reason for cancellation"),
  notifyCustomer: z
    .boolean()
    .optional()
    .describe("Whether to notify the customer"),
  refund: z.boolean().optional().describe("Whether to process a refund"),
  restock: z.boolean().optional().describe("Whether to restock items"),
});

export const CancelOrderDataSchema: z.ZodType<CancelOrderData> =
  CancelOrderData_;
export interface CancelOrderData extends z.infer<typeof CancelOrderData_> {}

// Full input schema
const CancelOrderInput_ = createOrderEditInputSchema(CancelOrderDataSchema);

export const CancelOrderInput: z.ZodType<CancelOrderInput> = CancelOrderInput_;
export interface CancelOrderInput extends z.infer<typeof CancelOrderInput_> {}

// Output data schema (empty - cancellation doesn't return specific data)
const CancelOrderOutputData_ = z.object({});

export const CancelOrderOutputDataSchema: z.ZodType<CancelOrderOutputData> =
  CancelOrderOutputData_;
export interface CancelOrderOutputData extends z.infer<
  typeof CancelOrderOutputData_
> {}

// Output metadata schema
const CancelOrderOutputMetadata_ = z.object({
  cancellationDetails: z
    .object({
      cancelledAt: z.string().datetime(),
      notificationSent: z.boolean(),
    })
    .optional(),
  refundDetails: z
    .object({
      amount: z.number(),
      currency: z.string(),
      processedAt: z.string().datetime().optional(),
    })
    .optional(),
});

export const CancelOrderOutputMetadataSchema: z.ZodType<CancelOrderOutputMetadata> =
  CancelOrderOutputMetadata_;
export interface CancelOrderOutputMetadata extends z.infer<
  typeof CancelOrderOutputMetadata_
> {}

// Full output schema
const CancelOrderOutput_ = createOrderEditOutputSchema(
  CancelOrderOutputDataSchema,
  CancelOrderOutputMetadata_,
);

export const CancelOrderOutput: z.ZodType<CancelOrderOutput> =
  CancelOrderOutput_;
export interface CancelOrderOutput extends z.infer<typeof CancelOrderOutput_> {}

/**
 * Self-serve cancel order input schema (no customer-controlled refund/restock options)
 * Used for customer-facing cancel flows where settings are determined by business logic
 */
const CancelOrderSelfServeInput_ = z.object({
  orderIdOrOrderNoOrConfirmationNo: z
    .string()
    .describe("The order number, ID, or confirmation number"),
  customerEmail: z
    .string()
    .describe("Customer's email address for verification"),
});

export const CancelOrderSelfServeInput: z.ZodType<CancelOrderSelfServeInput> =
  CancelOrderSelfServeInput_;
export interface CancelOrderSelfServeInput extends z.infer<
  typeof CancelOrderSelfServeInput_
> {}
